import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProfileType } from '@/types';

interface ProfileCardProps {
  profile: ProfileType;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}

// Abstract illustrations for each profile type
function ProfileIllustration({ profile }: { profile: ProfileType }) {
  const baseClasses = 'absolute transition-all duration-300';

  switch (profile) {
    case 'engineer':
      return (
        <div className="relative w-20 h-20">
          <div className={cn(baseClasses, 'top-2 left-2 w-14 h-10 bg-[#E94E1B]/20 rounded')} />
          <div className={cn(baseClasses, 'top-4 left-4 w-10 h-6 bg-[#E94E1B]/40 rounded-sm')} />
          <div
            className={cn(
              baseClasses,
              'bottom-2 right-2 w-8 h-8 border-2 border-[#E94E1B] rounded',
            )}
          />
        </div>
      );
    case 'product_manager':
      return (
        <div className="relative w-20 h-20">
          <div
            className={cn(
              baseClasses,
              'top-4 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#E94E1B] rounded-full',
            )}
          />
          <div className={cn(baseClasses, 'top-8 left-2 w-3 h-3 bg-[#E94E1B]/60 rounded-full')} />
          <div className={cn(baseClasses, 'top-8 right-2 w-3 h-3 bg-[#E94E1B]/60 rounded-full')} />
          <div
            className={cn(
              baseClasses,
              'bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-[#E94E1B]/40',
            )}
          />
        </div>
      );
    case 'designer':
      return (
        <div className="relative w-20 h-20">
          <div
            className={cn(
              baseClasses,
              'top-2 left-4 w-12 h-12 border-2 border-[#E94E1B] rounded-full',
            )}
          />
          <div className={cn(baseClasses, 'bottom-4 right-4 w-8 h-8 bg-[#E94E1B]/30 rotate-45')} />
        </div>
      );
    case 'founder':
      return (
        <div className="relative w-20 h-20">
          <div
            className={cn(
              baseClasses,
              'top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[30px] border-l-transparent border-r-transparent border-b-[#E94E1B]/40',
            )}
          />
          <div
            className={cn(baseClasses, 'bottom-2 left-1/2 -translate-x-1/2 w-16 h-2 bg-[#E94E1B]')}
          />
        </div>
      );
    case 'generalist':
      return (
        <div className="relative w-20 h-20">
          <div className={cn(baseClasses, 'top-4 left-4 w-6 h-6 bg-[#E94E1B]/30 rounded-full')} />
          <div className={cn(baseClasses, 'top-2 right-4 w-5 h-5 bg-[#E94E1B]/50 rounded')} />
          <div
            className={cn(baseClasses, 'bottom-4 left-6 w-4 h-8 bg-[#E94E1B]/40 rounded-full')}
          />
          <div
            className={cn(
              baseClasses,
              'bottom-2 right-2 w-6 h-6 border-2 border-[#E94E1B] rounded-full',
            )}
          />
        </div>
      );
  }
}

export function ProfileCard({ profile, label, description, selected, onSelect }: ProfileCardProps) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      className={cn(
        'relative w-full p-6 rounded-xl border-2 transition-all text-left cursor-pointer',
        selected
          ? 'bg-[#FDEAE3] border-[#E94E1B]'
          : 'bg-white border-[#CAC7C7] hover:border-[#E94E1B]/50 hover:bg-[#F4F2F2]',
      )}
    >
      {/* Checkmark */}
      {selected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-3 right-3 w-6 h-6 bg-[#E94E1B] rounded-full flex items-center justify-center"
        >
          <Check className="w-4 h-4 text-white" />
        </motion.div>
      )}

      <div className="flex items-start gap-4">
        {/* Illustration */}
        <div className="flex-shrink-0">
          <ProfileIllustration profile={profile} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg font-semibold text-[#2E2A23]">{label}</h3>
          <p className="text-sm text-[#888185] mt-1">{description}</p>
        </div>
      </div>
    </motion.button>
  );
}
